import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Mail, Phone, MapPin } from 'lucide-react';
import {
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaYoutube,
  FaGithub,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
} from 'react-icons/fa';

// import logo from '../assets/LOGO.png';
import logo from '../assets/MY LOGO.png';
const logoSrc = logo;

// Helper to render any icon safely
const Icon = ({ component: Comp, size = 18, className = '' }) => {
  if (Comp) {
    return <Comp size={size} className={className} />;
  }
  return (
    <span
      className={`inline-block rounded-full bg-gray-500 ${className}`}
      style={{ width: size, height: size }}
    />
  );
};

const Footer = () => {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="border-t border-white/5 bg-black/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* ─── Brand Column ─── */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3 group">
              {/* Logo Image */}
              <img
                src={logoSrc}
                alt="Isnap Media"
                className="h-10 w-auto object-contain transition-all duration-300 group-hover:scale-105"
              />
              {/* Brand Name – optional, you can remove this line if you want only the logo */}
              {/* <span className="font-serif text-xl font-bold text-white">Isnap Media</span> */}
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
              I’m a Creative Producer specialising in visual communication, combining design, motion, video, and photography to turn ideas into visual stories that connect
            </p>
            <div className="flex gap-3 pt-2">
              <a
                href="https://www.facebook.com/isnapmedia_solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                aria-label="Facebook"
              >
                <FaFacebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/isnapmedia_solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                aria-label="Instagram"
              >
                <FaInstagram className="w-4 h-4" />
              </a>
              <a
                href="https://linkedin.com/in/isnapmedia_solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <FaLinkedin className="w-4 h-4" />
              </a>
              <a
                href="https://x.com/isnapmedia_solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                aria-label="Twitter"
              >
                <FaTwitter className="w-4 h-4" />
              </a>
              <a
                href="https://youtube.com/user/isnapmedia_solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300"
                aria-label="YouTube"
              >
                <FaYoutube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* ─── Quick Links ─── */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {['About', 'Portfolio', 'Services', 'Contact'].map((item) => (
                <li key={item}>
                  <Link
                    to={`/${item.toLowerCase()}`}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 rounded-full bg-yellow-500/0 group-hover:bg-yellow-400 transition-all duration-200" />
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ─── Services ─── */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Services
            </h3>
            <ul className="space-y-2.5">
              {['Graphic Design', 'Videography', 'Photography', 'Motion Graphics'].map(
                (service) => (
                  <li key={service}>
                    <Link
                      to="/services"
                      className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center gap-2 group"
                    >
                      <span className="w-1 h-1 rounded-full bg-yellow-500/0 group-hover:bg-yellow-400 transition-all duration-200" />
                      {service}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* ─── Contact & Newsletter ─── */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
              Get in Touch
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 text-sm hover:text-white transition-colors duration-200">
                <FaMapMarkerAlt className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <span>West Adenta, Accra | GM-042-8370</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm hover:text-white transition-colors duration-200">
                <FaPhone className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <a href="tel:+233245258723">+233245258723 | +233574047270</a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm hover:text-white transition-colors duration-200">
                <FaEnvelope className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <a href="mailto:isnapmedia28@gmail.com">isnapmedia28@gmail.com</a>
              </li>
            </ul>

            {/* Newsletter */}
            <div className="mt-6">
              <h4 className="text-white font-semibold text-sm mb-2">Newsletter</h4>
              <p className="text-gray-400 text-xs mb-3">Get updates on new projects and offers.</p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition text-sm"
                  required
                />
                <button
                  type="submit"
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:shadow-lg hover:shadow-yellow-500/30 text-white text-sm px-4 py-2 rounded-lg transition font-medium whitespace-nowrap"
                >
                  {subscribed ? '✓ Subscribed' : 'Subscribe'}
                </button>
              </form>
              {subscribed && (
                <p className="text-green-400 text-xs mt-1">Thank you for subscribing!</p>
              )}
            </div>
          </div>
        </div>

        {/* ─── Bottom Bar ─── */}
        <div className="border-t border-white/5 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              &copy; {year} <span className="text-white font-medium">iSnap Media Solutions</span>.
              All Rights Reserved.
            </div>
            {/* <div className="flex items-center gap-6 text-sm text-gray-500">
              <span>Built with ❤️</span>
              <span className="hidden sm:inline text-white/10">|</span>
              <span className="text-xs tracking-wider">Crafted with precision</span>
            </div> */}
            <button
              onClick={scrollToTop}
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 hover:border-yellow-500/30 transition-all duration-300 group"
              aria-label="Back to top"
            >
              <Icon component={ArrowUp} size={18} className="group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;